<template lang="pug">
.articleCard(
  :class="positionClass"
  role="button"
  tabindex="0"
  @click="navigateToArticle"
  @keypress.enter="navigateToArticle"
)
  nuxt-img.articleCard__image(
    v-if="image"
    :src="image"
    :alt="`${title || ''} ${subTitle || ''}`"
    loading="lazy"
    fit="cover"
    width="100%"
    height="100%"
  )
  .articleCard__info.gap-2.d-flex.flex-column
    template(v-if="title || subTitle")
      EcreativArchetypesHeading.mb-0(
        v-if="title"
        tag="h3"
        size="2"
        fontWeight="bold"
        :context="textColor"
      ) {{ title }}
      EcreativArchetypesHeading.mb-0(
        v-if="subTitle"
        tag="h3"
        size="4"
        fontWeight="bold"
        :context="textColor"
      ) {{ subTitle }}
      EcreativArchePrimitivesParagraph(
        v-if="text"
        tag="h4"
        :context="textColor"
      ) {{ text }}
    .articleCard__link(v-if="linkText")
      EcreativArchetypesDestinationItemArchetype(
        componentType="InternalDestination"
        :linkColor="linkColor"
        fontWeight="bold"
        :text="linkText"
        isUnderLine="underline"
      )

    .articleCard__button(v-if="buttonText")
      EcreativArchetypesInteractiveArchetype(
        componentType="InternalDestination"
        :link="getUrl"
        :context="buttonContext"
        textColor="white"
        :text="buttonText"
        class="fw-bold justify-content-center"
        size="lg"
      )
</template>

<script lang="ts" setup>
/* eslint-disable */
import { computed } from 'vue';
import { useRouter } from '#imports';
import EcreativArchetypesHeading from '../../archetypes/Heading';
import EcreativArchetypesInteractiveArchetype from '../../archetypes/InteractiveArchetype';
import EcreativArchePrimitivesParagraph from '../../arche/primitives/Paragraph';

const props = withDefaults(
  defineProps<{
    image?: string;
    title?: string;
    subTitle?: string;
    text?: string;
    link?: string;
    category?: string;
    buttonText?: string;
    buttonContext?: string;
    buttonTextColor?: string;
    textColor?: string;
    position?: number; // 1 TL, 2 TR, 3 BL, 4 BR
    linkText?: string;
    linkColor?: string;
  }>(),
  {
    position: 1,
  },
);

const router = useRouter();

const getUrl = computed((): string => {
  if (!props.link) return '/';
  return props.category
    ? `/${props.category}/${props.link}`.replace('//', '/')
    : `/${props.link}`;
});

const navigateToArticle = (): void => {
  if (getUrl.value) router.push(getUrl.value);
};

const positionClass = computed(() => `articleCard--${props.position}`);
</script>

<style lang="scss" scoped>
$block: '.articleCard';

#{$block} {
  $block: &;

  &#{$block} {
    position: relative;

    // GRID STACK: image and info in same cell
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
    overflow: hidden;
    width: 100%;
    height: auto;
    background-color: $blue-100;
    cursor: pointer;
    border-radius: 1rem;
    container-type: inline-size;
    container-name: card;

    // Full component aspect on mobile; grow above
    aspect-ratio: 16/12;

    &:focus {
      outline: 2px solid var(--ti-primary);
      outline-offset: 2px;
    }

    @include media-breakpoint-up(sm) {
      min-height: 320px;
      aspect-ratio: unset;
    }
  }

  // Background image truly covers the whole card
  #{$block}__image {
    z-index: 0;
    grid-area: 1 / 1;
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: inherit;
  }

  // Text overlay: padding lives here (not on container)
  #{$block}__info {
    z-index: 1;
    grid-area: 1 / 1;

    // Fallback width + progressive enhancement via container queries
    max-width: 25ch;

    // Only the text gets spacing from edges
    padding: clamp(12px, 2.5vw, 32px);

    // Corner placement via Grid self-alignment (no absolute offsets)
    place-self: var(--ai, start) var(--ji, start); // start|center|end // start|center|end

    @supports (container-type: inline-size) {
      max-width: 100%;

      @container card (min-width: 375px) {
        max-width: 24ch;
      }

      @container card (min-width: 400px) {
        max-width: 28ch;
      }

      @container card (min-width: 500px) {
        max-width: 40ch;
      }

      @container card (min-width: 600px) {
        max-width: 48ch;
      }
    }
  }

  #{$block}__button {
    @supports (container-type: inline-size) {
      max-width: 80%;

      @container card (min-width: 200px) {
        max-width: 32ch;
      }

      @container card (min-width: 400px) {
        max-width: 24ch;
      }
    }
  }
}

// Corner modifiers using Grid self-alignment
#{$block} {
  $block: &;

  &#{$block}--1 {
    // top-left
    --ai: start;
    --ji: start;
  }

  &#{$block}--2 {
    // top-right
    --ai: start;
    --ji: end;
  }

  &#{$block}--3 {
    // bottom-left
    --ai: end;
    --ji: start;
  }

  &#{$block}--4 {
    // bottom-right
    --ai: end;
    --ji: end;
  }
}
</style>
